/**
 * Operations functional pass: the daily command center.
 *
 * Operations coordinates; source modules execute. Nothing here copies a
 * work order, a route, a visitor or an absence — it reads their current
 * facts and decides readiness from explicit, configured requirements. Every
 * readiness state comes with the exact reasons for it; there is no score.
 */

import type { BaseDocument } from '../platform/documents.js'

// ─────────────────────────────────────────────────────────────────────────────
// Readiness
// ─────────────────────────────────────────────────────────────────────────────

export const READINESS_STATES = ['ready', 'needs_attention', 'critical', 'not_reviewed', 'closed', 'emergency'] as const
export type ReadinessState = (typeof READINESS_STATES)[number]
export const READINESS_LABELS: Record<ReadinessState, string> = { ready: 'Ready', needs_attention: 'Needs Attention', critical: 'Critical Issue', not_reviewed: 'Not Yet Reviewed', closed: 'Closed', emergency: 'Emergency Mode' }

export const OPS_SEVERITIES = ['informational', 'routine', 'elevated', 'urgent', 'critical'] as const
export type OpsSeverity = (typeof OPS_SEVERITIES)[number]
export const SEVERITY_MEANING: Record<OpsSeverity, string> = {
  informational: 'For awareness; no action needed today.',
  routine: 'Handle in the normal course of the day.',
  elevated: 'Affects part of the day; handle before it spreads.',
  urgent: 'Affects instruction or safety now; act within the hour.',
  critical: 'Safety, closure or whole-building risk; act immediately.',
}
const SEV_RANK: Record<OpsSeverity, number> = { informational: 0, routine: 1, elevated: 2, urgent: 3, critical: 4 }
export const severityAtLeast = (s: OpsSeverity, min: OpsSeverity) => SEV_RANK[s] >= SEV_RANK[min]

/** One fact behind a readiness state, with where it came from. */
export interface ReadinessReason {
  component: string
  severity: OpsSeverity
  text: string
  source: { module: string; label: string; to: string | null; entityId?: string | null }
}

/** The readiness components a district may require, each read from its source. */
export const READINESS_COMPONENTS = ['opening_checklist', 'facilities', 'coverage', 'transportation', 'food_service', 'health_office', 'visitors', 'inspections', 'events', 'issues'] as const
export type ReadinessComponent = (typeof READINESS_COMPONENTS)[number]
export const COMPONENT_LABELS: Record<ReadinessComponent, string> = {
  opening_checklist: 'Opening checklist',
  facilities: 'Critical facilities',
  coverage: 'Staff coverage',
  transportation: 'Transportation',
  food_service: 'Food service',
  health_office: 'Health office coverage',
  visitors: 'Visitor station',
  inspections: 'Required inspections',
  events: 'Event setup',
  issues: 'Operational issues',
}

export interface ReadinessRules {
  required: ReadinessComponent[]
  /** A bus delayed at least this long counts against readiness. */
  transportDelayMinutes: number
  /** Coverage gaps for these roles count against readiness (teacher, nurse, driver…). */
  coverageRoles: string[]
  /** Open operational issues at or above this severity count against readiness. */
  issueSeverity: OpsSeverity
}

export interface ReadinessFacts {
  closed: { reason: string } | null
  emergency: { title: string; to: string } | null
  /** After an emergency or closure: the reopening checklist must be complete before Ready. */
  reopeningRequired: boolean
  opening: { exists: boolean; complete: boolean; failedItems: string[]; dueTime: string | null; overdue: boolean }
  reasons: ReadinessReason[]
}

/**
 * OperationsReadinessService, as a pure rule: closed and emergency override;
 * any critical reason makes it Critical; any other reason makes it Needs
 * Attention; an opening checklist not yet done keeps it Not Yet Reviewed
 * (or Needs Attention once it is overdue). Otherwise Ready.
 */
export function evaluateReadiness(rules: ReadinessRules, f: ReadinessFacts): { state: ReadinessState; reasons: ReadinessReason[]; unmet: string[]; critical: ReadinessReason[] } {
  if (f.emergency) return { state: 'emergency', reasons: [{ component: 'issues', severity: 'critical', text: `Emergency: ${f.emergency.title}`, source: { module: 'Emergency', label: f.emergency.title, to: f.emergency.to } }], unmet: [], critical: [] }
  if (f.closed) return { state: 'closed', reasons: [{ component: 'issues', severity: 'informational', text: f.closed.reason, source: { module: 'Calendar', label: f.closed.reason, to: '/calendar' } }], unmet: [], critical: [] }
  const reasons = f.reasons.filter((r) => rules.required.includes(r.component as ReadinessComponent) || r.severity === 'critical')
  const unmet: string[] = []
  if (rules.required.includes('opening_checklist') && f.opening.exists && !f.opening.complete) unmet.push(f.opening.overdue ? `Opening checklist not complete (due ${f.opening.dueTime ?? 'at opening'})` : 'Opening checklist in progress')
  if (f.reopeningRequired) unmet.push('Reopening checklist not complete')
  for (const item of f.opening.failedItems) reasons.push({ component: 'opening_checklist', severity: 'elevated', text: `Opening check failed: ${item}`, source: { module: 'Operations', label: 'Opening checklist', to: null } })
  const critical = reasons.filter((r) => r.severity === 'critical')
  if (critical.length) return { state: 'critical', reasons, unmet, critical }
  if (reasons.length || f.reopeningRequired || (unmet.length && f.opening.overdue)) return { state: 'needs_attention', reasons, unmet, critical }
  if (unmet.length) return { state: 'not_reviewed', reasons, unmet, critical }
  return { state: 'ready', reasons, unmet, critical }
}

// ─────────────────────────────────────────────────────────────────────────────
// Checklists and inspections
// ─────────────────────────────────────────────────────────────────────────────

export const CHECKLIST_KINDS = ['opening', 'closing', 'reopening', 'inspection', 'event', 'custom'] as const
export type ChecklistKind = (typeof CHECKLIST_KINDS)[number]
export const VERIFICATION_TYPES = ['checkbox', 'yes_no', 'numeric', 'photo', 'comment', 'initial', 'scan', 'source'] as const
export type VerificationType = (typeof VERIFICATION_TYPES)[number]
export const DAY_TYPES_OPS = ['school_day', 'delayed_start', 'early_release', 'event_day', 'weekend', 'summer', 'closed', 'recovery'] as const
export type OpsDayType = (typeof DAY_TYPES_OPS)[number]

export interface OpsChecklistItem {
  key: string
  title: string
  description?: string | null
  category: string
  required: boolean
  assignedRole: string | null
  /** HH:MM on a normal day; shifted by the day's offset on delayed starts. */
  dueTime: string | null
  verification: { type: VerificationType; min?: number | null; max?: number | null; unit?: string | null; scanCode?: string | null }
  /** issue_task: an operational issue owned by the item's team plus a follow-up task (freezer out of range). */
  failureAction: 'issue' | 'work_order' | 'task' | 'issue_task' | 'none'
  failureSeverity: OpsSeverity
  /** A district check a school may not remove. */
  locked: boolean
  /** Still required when the school is closed (boiler, freezer, alarm). */
  criticalWhenClosed: boolean
  sourceLink?: { module: string; to: string } | null
  assetId?: string | null
  locationId?: string | null
  sequence: number
}

export interface ChecklistTemplate extends BaseDocument {
  docType: 'opsChecklistTemplate'
  key: string
  name: string
  kind: ChecklistKind
  /** District templates apply to every school unless a school template of the same kind replaces them; locked items carry over. */
  scope: 'district' | 'school'
  schoolId: string | null
  locationId?: string | null
  /** Inspections: the kind of Facilities location each school's instance runs at (kitchen, athletic…). */
  locationKind?: string | null
  dayTypes: OpsDayType[]
  /** Inspections: how often an instance is generated. */
  frequency?: Recurrence | null
  responsibleRole?: string | null
  items: OpsChecklistItem[]
  status: 'active' | 'retired'
  version: number
  history: { at: string; byName: string; action: string }[]
}

export interface ItemResult {
  key: string
  result: 'pass' | 'fail' | 'na'
  value?: number | string | null
  note?: string | null
  severity?: OpsSeverity | null
  followUp?: string | null
  photoBlobPath?: string | null
  scanned?: string | null
  byName: string
  byUserId: string
  at: string
  issueId?: string | null
  workOrderId?: string | null
  taskId?: string | null
}

export interface ChecklistRun extends BaseDocument {
  docType: 'opsChecklistRun'
  templateId: string
  templateKey: string
  templateVersion: number
  name: string
  kind: ChecklistKind
  schoolId: string | null
  locationId?: string | null
  date: string
  /** Minutes added to every due time (a two-hour delay is +120). */
  dueOffsetMinutes: number
  items: OpsChecklistItem[]
  results: ItemResult[]
  status: 'open' | 'complete' | 'failed' | 'suppressed'
  suppressedReason?: string | null
  completedAt?: string | null
  completedByName?: string | null
  eventPlanId?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

/** A numeric reading against its configured range; no global thresholds. */
export function numericOutcome(v: OpsChecklistItem['verification'], value: number): { ok: boolean; message: string | null } {
  if (v.min != null && value < v.min) return { ok: false, message: `${value}${v.unit ?? ''} is below the configured minimum ${v.min}${v.unit ?? ''}` }
  if (v.max != null && value > v.max) return { ok: false, message: `${value}${v.unit ?? ''} is above the configured maximum ${v.max}${v.unit ?? ''}` }
  return { ok: true, message: null }
}

/** What a failed item must carry: a note, a severity and the follow-up. */
export function failureGaps(r: Pick<ItemResult, 'result' | 'note' | 'severity' | 'followUp'>): string[] {
  if (r.result !== 'fail') return []
  const out: string[] = []
  if (!r.note?.trim()) out.push('a note')
  if (!r.severity) out.push('a severity')
  if (!r.followUp?.trim()) out.push('the follow-up action')
  return out
}

/** Required items done (pass or explained fail) → complete; any fail → 'failed' status (complete with failures). */
export function runStatus(items: readonly OpsChecklistItem[], results: readonly ItemResult[]): 'open' | 'complete' | 'failed' {
  const by = new Map(results.map((r) => [r.key, r]))
  const pending = items.filter((i) => i.required && !by.get(i.key))
  if (pending.length) return 'open'
  return results.some((r) => r.result === 'fail') ? 'failed' : 'complete'
}

/** HH:MM + minutes. */
export function shiftTime(t: string | null, minutes: number): string | null {
  if (!t) return null
  const [h, m] = t.split(':').map(Number)
  const x = Math.max(0, Math.min(24 * 60 - 1, (h ?? 0) * 60 + (m ?? 0) + minutes))
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`
}

/** District base + a school's own additions; locked district items cannot be removed by the school. */
export function mergeChecklist(district: ChecklistTemplate | null, school: ChecklistTemplate | null): OpsChecklistItem[] {
  if (!school) return district?.items ?? []
  if (!district) return school.items
  const locked = district.items.filter((i) => i.locked)
  const schoolKeys = new Set(school.items.map((i) => i.key))
  return [...locked.filter((i) => !schoolKeys.has(i.key)), ...school.items].sort((a, b) => a.sequence - b.sequence)
}

// ─────────────────────────────────────────────────────────────────────────────
// Issues and tasks
// ─────────────────────────────────────────────────────────────────────────────

export const ISSUE_STATUSES = ['new', 'assigned', 'in_progress', 'waiting', 'escalated', 'resolved', 'verified', 'closed', 'cancelled'] as const
export type IssueStatus = (typeof ISSUE_STATUSES)[number]
export const OPEN_ISSUE: IssueStatus[] = ['new', 'assigned', 'in_progress', 'waiting', 'escalated']

export interface OpsOwner {
  kind: 'staff' | 'role' | 'team'
  staffId?: string | null
  role?: string | null
  teamKey?: string | null
  label: string
}

export interface OpsSourceLink {
  module: string
  entityId: string
  label: string
  to: string | null
}

export interface OperationalIssue extends BaseDocument {
  docType: 'opsIssue'
  number: string
  title: string
  description?: string | null
  schoolId: string | null
  locationId?: string | null
  category: string
  severity: OpsSeverity
  /** The record that owns the work, when another module owns it. Never copied. */
  source?: OpsSourceLink | null
  related: OpsSourceLink[]
  owner: OpsOwner | null
  dueAt?: string | null
  status: IssueStatus
  openedAt: string
  openedByName: string
  resolvedAt?: string | null
  verifiedAt?: string | null
  closedAt?: string | null
  resolution?: string | null
  /** Escalation steps already sent, so a sweep never sends one twice. */
  escalations: { ruleKey: string; step: number; at: string; to: string }[]
  /** Utility outages: which service, and what it affects. */
  outage?: { utility: 'power' | 'water' | 'gas' | 'internet' | 'hvac'; affected: string[] } | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
  idempotencyKey?: string | null
}

export const issueNumber = (year: number, n: number) => `OPS-${year}-${String(n).padStart(6, '0')}`

export const TASK_STATUSES = ['not_started', 'in_progress', 'blocked', 'waiting', 'complete', 'cancelled'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export interface Recurrence {
  freq: 'daily' | 'weekly' | 'monthly' | 'weekdays' | 'school_days' | 'custom'
  /** 0–6 for weekly/custom. */
  days?: number[]
  dayOfMonth?: number | null
  time: string
}

export interface OpsTask extends BaseDocument {
  docType: 'opsTask'
  title: string
  description?: string | null
  category: string
  schoolId: string | null
  locationId?: string | null
  source?: OpsSourceLink | null
  assignee: OpsOwner
  dueAt: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  status: TaskStatus
  completedAt?: string | null
  completedByName?: string | null
  completionNote?: string | null
  attachments: { blobPath: string; fileName: string; at: string }[]
  recurringId?: string | null
  checklistRunId?: string | null
  eventPlanId?: string | null
  issueId?: string | null
  /** A closure suppresses school-day tasks; critical ones stay. */
  suppressedReason?: string | null
  critical?: boolean
  escalations: { ruleKey: string; step: number; at: string; to: string }[]
  history: { at: string; byName: string; action: string; note?: string | null }[]
  idempotencyKey?: string | null
}

export interface RecurringTask extends BaseDocument {
  docType: 'opsRecurringTask'
  title: string
  description?: string | null
  category: string
  schoolId: string | null
  locationId?: string | null
  assignee: OpsOwner
  priority: OpsTask['priority']
  recurrence: Recurrence
  critical: boolean
  active: boolean
  lastGeneratedFor?: string | null
}

/** Whether a recurring task falls on a date (school days need the calendar's answer). */
export function recursOn(r: Recurrence, date: string, isSchoolDay: boolean): boolean {
  const dow = new Date(`${date}T12:00:00Z`).getUTCDay()
  if (r.freq === 'daily') return true
  if (r.freq === 'weekdays') return dow >= 1 && dow <= 5
  if (r.freq === 'school_days') return isSchoolDay
  if (r.freq === 'weekly' || r.freq === 'custom') return (r.days ?? [1]).includes(dow)
  if (r.freq === 'monthly') return Number(date.slice(8, 10)) === (r.dayOfMonth ?? 1)
  return false
}

// ─────────────────────────────────────────────────────────────────────────────
// Escalation
// ─────────────────────────────────────────────────────────────────────────────

export interface EscalationRule {
  key: string
  label: string
  appliesTo: 'task' | 'issue'
  when: { minSeverity?: OpsSeverity | null; categories?: string[]; priority?: OpsTask['priority'][] }
  /** Minutes after due (tasks) or after opening (issues), with who hears. */
  steps: { afterMinutes: number; notifyRoles: string[]; markEscalated: boolean }[]
}

/** Steps now due and not yet sent — the sweep is idempotent by (rule, step). */
export function dueEscalations(rule: EscalationRule, anchorIso: string, nowIso: string, sent: readonly { ruleKey: string; step: number }[]): { step: number; notifyRoles: string[]; markEscalated: boolean }[] {
  const elapsed = (Date.parse(nowIso) - Date.parse(anchorIso)) / 60_000
  return rule.steps.map((s, i) => ({ ...s, step: i })).filter((s) => elapsed >= s.afterMinutes && !sent.some((x) => x.ruleKey === rule.key && x.step === s.step))
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────────────────

export interface OpsTeam {
  key: string
  label: string
  /** Staff ids and/or roles; resolved at runtime so turnover never orphans a task. */
  staffIds: string[]
  roles: string[]
}

export interface OpsSettings {
  categories: { key: string; label: string }[]
  readiness: ReadinessRules
  teams: OpsTeam[]
  /** Role → who holds it per school (Head Custodian at Riverview High…). */
  roleAssignments: { role: string; label: string; schoolId: string | null; staffId: string }[]
  escalations: EscalationRule[]
  deliveryTypes: string[]
  deliveryLocations: string[]
  /** Offsets for special day types, e.g. delayed start +120. */
  dayOffsets: { delayed_start: number; early_release: number }
  nextIssueNumber: number
  /** Event support templates; the shipped ones when absent. */
  eventTemplates?: EventTemplate[]
  /** Minutes after expected before a delivery shows as late. */
  deliveryGraceMinutes?: number
  operationsLeadStaffId?: string | null
  onCall?: { facilitiesStaffId?: string | null; administratorStaffId?: string | null }
}

export interface OpsSettingsDocument extends BaseDocument, OpsSettings {
  docType: 'opsSettings'
}

export const DEFAULT_OPS_SETTINGS: OpsSettings = {
  categories: ['staffing', 'facilities', 'transportation', 'food_service', 'security', 'technology', 'event_support', 'delivery', 'safety', 'supplies', 'weather', 'utilities', 'other'].map((k) => ({ key: k, label: k.replace('_', ' ').replace(/^./, (c) => c.toUpperCase()) })),
  readiness: { required: ['opening_checklist', 'facilities', 'coverage', 'transportation', 'food_service', 'health_office', 'issues'], transportDelayMinutes: 15, coverageRoles: ['teacher', 'nurse', 'driver'], issueSeverity: 'urgent' },
  teams: [
    { key: 'facilities', label: 'Facilities', staffIds: [], roles: ['facilities_staff'] },
    { key: 'custodial', label: 'Custodial', staffIds: [], roles: ['facilities_staff'] },
    { key: 'transportation', label: 'Transportation', staffIds: [], roles: ['transportation_operations'] },
    { key: 'food_service', label: 'Food Service', staffIds: [], roles: [] },
    { key: 'security', label: 'Security', staffIds: [], roles: [] },
    { key: 'technology', label: 'Technology', staffIds: [], roles: ['it_admin'] },
    { key: 'administration', label: 'Administration', staffIds: [], roles: ['principal', 'school_admin'] },
    { key: 'event_support', label: 'Event Support', staffIds: [], roles: ['operations_manager'] },
  ],
  roleAssignments: [],
  escalations: [
    { key: 'task-overdue', label: 'Overdue operational task', appliesTo: 'task', when: {}, steps: [{ afterMinutes: 15, notifyRoles: [], markEscalated: false }, { afterMinutes: 30, notifyRoles: ['operations_manager'], markEscalated: false }, { afterMinutes: 45, notifyRoles: ['principal', 'school_admin'], markEscalated: true }] },
    { key: 'critical-issue', label: 'Critical operational issue', appliesTo: 'issue', when: { minSeverity: 'critical' }, steps: [{ afterMinutes: 0, notifyRoles: ['operations_manager'], markEscalated: false }, { afterMinutes: 10, notifyRoles: ['principal'], markEscalated: true }, { afterMinutes: 20, notifyRoles: ['district_admin'], markEscalated: true }] },
    { key: 'urgent-issue', label: 'Urgent operational issue', appliesTo: 'issue', when: { minSeverity: 'urgent' }, steps: [{ afterMinutes: 0, notifyRoles: ['operations_manager'], markEscalated: false }, { afterMinutes: 60, notifyRoles: ['principal'], markEscalated: true }] },
  ],
  deliveryTypes: ['food_service', 'supplies', 'technology', 'furniture', 'equipment', 'freight', 'vendor_service', 'other'],
  deliveryLocations: ['Dock', 'Main Office', 'Kitchen', 'Warehouse', 'Technology', 'Facilities', 'Other'],
  dayOffsets: { delayed_start: 120, early_release: 0 },
  nextIssueNumber: 1,
  deliveryGraceMinutes: 15,
}

const item = (key: string, title: string, sequence: number, o: Partial<OpsChecklistItem> = {}): OpsChecklistItem => ({ key, title, category: 'building', required: true, assignedRole: 'facilities_staff', dueTime: '06:45', verification: { type: 'yes_no' }, failureAction: 'issue', failureSeverity: 'elevated', locked: false, criticalWhenClosed: false, sequence, ...o })

/** Shipped district templates; each school may add its own items. */
export const DEFAULT_CHECKLISTS: Omit<ChecklistTemplate, keyof BaseDocument | 'docType' | 'history'>[] = [
  {
    key: 'opening',
    name: 'Opening checklist',
    kind: 'opening',
    scope: 'district',
    schoolId: null,
    dayTypes: ['school_day', 'delayed_start', 'early_release', 'event_day'],
    status: 'active',
    version: 1,
    items: [
      item('entrance', 'Main entrance unlocked', 1, { category: 'security', dueTime: '06:30', locked: true }),
      item('alarm', 'Alarm status checked and disarmed', 2, { category: 'security', dueTime: '06:30', locked: true, criticalWhenClosed: true }),
      item('hvac', 'Boiler / HVAC operating', 3, { category: 'facilities', failureAction: 'work_order', failureSeverity: 'urgent', criticalWhenClosed: true }),
      item('restrooms', 'Restrooms operational', 4, { category: 'facilities', failureAction: 'work_order' }),
      item('cafeteria', 'Cafeteria ready', 5, { category: 'food_service', assignedRole: null, verification: { type: 'source' }, sourceLink: { module: 'Cafeteria', to: '/cafeteria' } }),
      item('health', 'Health office staffed', 6, { category: 'staffing', assignedRole: 'administrative_staff', verification: { type: 'source' }, sourceLink: { module: 'Absences & Substitutes', to: '/absences?tab=coverage' } }),
      item('transport', 'Transportation status reviewed', 7, { category: 'transportation', assignedRole: 'administrative_staff', verification: { type: 'source' }, sourceLink: { module: 'Transportation', to: '/transportation' } }),
      item('visitor-station', 'Visitor station ready', 8, { category: 'security', assignedRole: 'administrative_staff', dueTime: '07:00' }),
      item('radios', 'Emergency radios checked', 9, { category: 'safety', locked: true, verification: { type: 'initial' } }),
    ],
  },
  {
    key: 'closing',
    name: 'Closing checklist',
    kind: 'closing',
    scope: 'district',
    schoolId: null,
    dayTypes: ['school_day', 'delayed_start', 'early_release', 'event_day'],
    status: 'active',
    version: 1,
    items: [
      item('visitors-cleared', 'Visitors cleared', 1, { category: 'security', assignedRole: 'administrative_staff', dueTime: '16:00', verification: { type: 'source' }, sourceLink: { module: 'Visitors', to: '/visitors' } }),
      item('doors', 'Exterior doors secured', 2, { category: 'security', dueTime: '18:00', locked: true, criticalWhenClosed: true }),
      item('rooms', 'Major rooms checked', 3, { dueTime: '18:00' }),
      item('custodial', 'Custodial issues logged', 4, { dueTime: '18:00', verification: { type: 'comment' }, required: false }),
      item('overnight', 'Overnight facilities issues noted', 5, { dueTime: '18:00', verification: { type: 'comment' }, required: false }),
      item('event-spaces', 'Event spaces cleared', 6, { dueTime: '22:00', required: false }),
      item('alarm-set', 'Alarm / security handoff complete', 7, { category: 'security', dueTime: '22:30', locked: true, verification: { type: 'initial' }, criticalWhenClosed: true }),
    ],
  },
  {
    key: 'reopening',
    name: 'Reopening checklist',
    kind: 'reopening',
    scope: 'district',
    schoolId: null,
    dayTypes: ['recovery'],
    status: 'active',
    version: 1,
    items: [
      item('re-facilities', 'Building inspected; no open safety work orders', 1, { category: 'facilities', failureSeverity: 'urgent', locked: true }),
      item('re-safety', 'Safety walk complete (exits, alarms, AEDs)', 2, { category: 'safety', locked: true, verification: { type: 'initial' } }),
      item('re-utilities', 'Utilities restored (power, water, HVAC, internet)', 3, { category: 'utilities', failureAction: 'work_order', failureSeverity: 'critical', locked: true }),
      item('re-transport', 'Transportation ready', 4, { category: 'transportation', assignedRole: 'transportation_operations', verification: { type: 'source' }, sourceLink: { module: 'Transportation', to: '/transportation' } }),
      item('re-food', 'Food service ready', 5, { category: 'food_service', assignedRole: null, verification: { type: 'source' }, sourceLink: { module: 'Cafeteria', to: '/cafeteria' } }),
    ],
  },
]

// ─────────────────────────────────────────────────────────────────────────────
// Events & Support (stage 2) — the Calendar owns the event; Operations owns the support plan
// ─────────────────────────────────────────────────────────────────────────────

export const SUPPORT_AREAS = ['setup', 'security', 'custodial', 'technology', 'food_service', 'transportation', 'equipment', 'check_in', 'parking', 'cleanup'] as const
export type SupportArea = (typeof SUPPORT_AREAS)[number]
export const SUPPORT_AREA_LABELS: Record<SupportArea, string> = { setup: 'Setup', security: 'Security', custodial: 'Custodial', technology: 'Technology', food_service: 'Food service / concessions', transportation: 'Transportation', equipment: 'Equipment', check_in: 'Visitor check-in', parking: 'Parking', cleanup: 'Cleanup' }

export interface SupportItemSpec {
  key: string
  area: SupportArea
  title: string
  /** Team key from settings; resolved to people when the reminder is sent. */
  teamKey: string
  /** Minutes from the event start (or its end, with `fromEnd`); negative is before. */
  offsetMinutes: number
  /** Count from the event's end instead of its start (cleanup, lockup). */
  fromEnd?: boolean
}

export interface EventTemplate {
  key: string
  label: string
  /** Words in an event title that suggest this template ("football", "varsity"). */
  keywords: string[]
  items: SupportItemSpec[]
}

const spec = (key: string, area: SupportArea, title: string, teamKey: string, offsetMinutes: number, fromEnd = false): SupportItemSpec => ({ key, area, title, teamKey, offsetMinutes, ...(fromEnd ? { fromEnd } : {}) })

export const DEFAULT_EVENT_TEMPLATES: EventTemplate[] = [
  { key: 'football', label: 'Football game', keywords: ['football', 'varsity football', 'homecoming game'], items: [spec('stadium-open', 'setup', 'Stadium open; restrooms checked', 'facilities', -150), spec('lights', 'equipment', 'Field lights on and tested', 'facilities', -120), spec('gates', 'security', 'Security at gates', 'security', -90), spec('concessions', 'food_service', 'Concessions ready', 'food_service', -60), spec('parking', 'parking', 'Parking lot attendants in place', 'security', -60), spec('press-box', 'technology', 'Press box audio and scoreboard checked', 'technology', -90), spec('cleanup', 'cleanup', 'Stadium cleanup', 'custodial', 30, true), spec('lockup', 'security', 'Gates locked; lights off', 'facilities', 60, true)] },
  { key: 'basketball', label: 'Basketball game', keywords: ['basketball'], items: [spec('gym-setup', 'setup', 'Gym bleachers out; scorer’s table set', 'custodial', -90), spec('scoreboard', 'technology', 'Scoreboard and sound tested', 'technology', -60), spec('door', 'security', 'Door and ticket table staffed', 'security', -45), spec('concessions', 'food_service', 'Concessions ready', 'food_service', -45), spec('cleanup', 'cleanup', 'Gym cleanup', 'custodial', 30, true)] },
  { key: 'graduation', label: 'Graduation', keywords: ['graduation', 'commencement'], items: [spec('chairs', 'setup', 'Chairs and stage set', 'custodial', -240), spec('sound', 'technology', 'Sound, microphones and livestream tested', 'technology', -120), spec('security', 'security', 'Security plan briefed; entrances staffed', 'security', -90), spec('parking', 'parking', 'Parking and accessible drop-off staffed', 'security', -90), spec('checkin', 'check_in', 'Graduate check-in table', 'administration', -75), spec('cleanup', 'cleanup', 'Venue cleanup', 'custodial', 30, true)] },
  { key: 'parent_night', label: 'Parent night / open house', keywords: ['open house', 'parent night', 'curriculum night', 'back to school'], items: [spec('signage', 'setup', 'Signage and room maps posted', 'custodial', -90), spec('checkin', 'check_in', 'Visitor check-in staffed', 'administration', -30), spec('doors', 'security', 'Entrances unlocked and staffed', 'security', -30), spec('cleanup', 'cleanup', 'Building walk and lockup', 'custodial', 30, true)] },
  { key: 'board_meeting', label: 'Board meeting', keywords: ['board meeting', 'board of education'], items: [spec('room', 'setup', 'Board room set (tables, name plates, public seating)', 'custodial', -90), spec('av', 'technology', 'Microphones, projector and livestream tested', 'technology', -60), spec('door', 'security', 'Front door staffed', 'security', -30), spec('cleanup', 'cleanup', 'Room reset', 'custodial', 30, true)] },
  { key: 'concert', label: 'Concert / performance', keywords: ['concert', 'choir', 'band', 'orchestra', 'musical', 'play', 'performance'], items: [spec('risers', 'setup', 'Risers, chairs and stands set', 'custodial', -120), spec('sound', 'technology', 'Sound and lighting checked', 'technology', -90), spec('doors', 'security', 'Doors and ticket table staffed', 'security', -45), spec('cleanup', 'cleanup', 'Auditorium cleanup', 'custodial', 30, true)] },
  { key: 'dance', label: 'Dance', keywords: ['dance', 'prom', 'formal'], items: [spec('setup', 'setup', 'Venue set and decorated', 'custodial', -180), spec('dj', 'technology', 'DJ power and sound checked', 'technology', -60), spec('security', 'security', 'Security and chaperones briefed', 'security', -45), spec('checkin', 'check_in', 'Student check-in table', 'administration', -30), spec('cleanup', 'cleanup', 'Venue cleanup', 'custodial', 30, true)] },
  { key: 'testing_day', label: 'Testing day', keywords: ['testing', 'state test', 'sat', 'act', 'psat', 'ap exam'], items: [spec('rooms', 'setup', 'Testing rooms set; clocks visible', 'custodial', -90), spec('devices', 'technology', 'Testing devices charged and checked', 'technology', -90), spec('quiet', 'security', 'Quiet-zone signage; bells silenced', 'administration', -30)] },
  { key: 'conference', label: 'Parent–teacher conferences', keywords: ['conference', 'conferences'], items: [spec('tables', 'setup', 'Conference tables and waiting chairs', 'custodial', -60), spec('checkin', 'check_in', 'Visitor check-in staffed', 'administration', -15), spec('cleanup', 'cleanup', 'Building walk and lockup', 'custodial', 30, true)] },
]

/** The template whose keywords appear in an event title, or null. */
export function suggestTemplate(title: string, templates: readonly EventTemplate[] = DEFAULT_EVENT_TEMPLATES): EventTemplate | null {
  const t = title.toLowerCase()
  let best: { tpl: EventTemplate; len: number } | null = null
  for (const tpl of templates) for (const k of tpl.keywords) if (t.includes(k) && (!best || k.length > best.len)) best = { tpl, len: k.length }
  return best?.tpl ?? null
}

export interface EventSnapshot { title: string; date: string; startTime: string | null; endTime: string | null; location: string | null; status: string }

export interface SupportPlanItem {
  key: string
  area: SupportArea
  title: string
  owner: OpsOwner
  offsetMinutes: number
  fromEnd?: boolean
  taskId?: string | null
  note?: string | null
}

export interface EventSupportPlan extends BaseDocument {
  docType: 'opsEventPlan'
  eventId: string
  schoolId: string | null
  templateKey: string | null
  /** The event as it was when the plan was last reconciled; the Calendar stays the owner. */
  snapshot: EventSnapshot
  items: SupportPlanItem[]
  status: 'draft' | 'active' | 'cancelled' | 'completed'
  /** Set when the event changed in a way someone must look at (cancelled, moved). */
  needsReview?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

/** HH:MM on a date plus minutes → { date, time } (crosses midnight correctly). */
export function addMinutesLocal(date: string, time: string, minutes: number): { date: string; time: string } {
  const [h, m] = time.split(':').map(Number)
  const base = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), h ?? 0, m ?? 0)
  const x = new Date(base + minutes * 60_000)
  return { date: x.toISOString().slice(0, 10), time: x.toISOString().slice(11, 16) }
}

/** When a support item is due, from the event's own times. */
export function supportDue(snap: Pick<EventSnapshot, 'date' | 'startTime' | 'endTime'>, item: Pick<SupportItemSpec, 'offsetMinutes' | 'fromEnd'>): { date: string; time: string } {
  const anchor = item.fromEnd ? (snap.endTime ?? snap.startTime ?? '17:00') : (snap.startTime ?? '17:00')
  return addMinutesLocal(snap.date, anchor, item.offsetMinutes)
}

const toMin = (d: string, t: string | null) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) / 60_000 + (t ? Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) : 0)

/** What changed in the Calendar since the plan's snapshot. */
export function reconcileEvent(snap: EventSnapshot, now: EventSnapshot): { change: 'none' | 'cancelled' | 'rescheduled' | 'details'; deltaMinutes: number; text: string | null } {
  if (now.status === 'cancelled' && snap.status !== 'cancelled') return { change: 'cancelled', deltaMinutes: 0, text: 'The Calendar event was cancelled.' }
  const delta = toMin(now.date, now.startTime) - toMin(snap.date, snap.startTime)
  if (delta !== 0 || now.endTime !== snap.endTime) return { change: 'rescheduled', deltaMinutes: delta, text: `The event moved from ${snap.date} ${snap.startTime ?? ''} to ${now.date} ${now.startTime ?? ''}.`.replace(/\s+\./, '.') }
  if (now.location !== snap.location || now.title !== snap.title) return { change: 'details', deltaMinutes: 0, text: `Event details changed${now.location !== snap.location ? ` (location: ${now.location ?? 'none'})` : ''}.` }
  return { change: 'none', deltaMinutes: 0, text: null }
}

// ─────────────────────────────────────────────────────────────────────────────
// Deliveries (stage 2) — logistics only; Purchasing owns receiving
// ─────────────────────────────────────────────────────────────────────────────

export const DELIVERY_STATUSES = ['expected', 'arrived', 'delayed', 'received', 'redirected', 'refused', 'cancelled'] as const
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number]
export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = { expected: 'Expected', arrived: 'Arrived', delayed: 'Delayed', received: 'Received', redirected: 'Redirected', refused: 'Refused', cancelled: 'Cancelled' }
export const DELIVERY_MOVES: Record<DeliveryStatus, readonly DeliveryStatus[]> = {
  expected: ['arrived', 'delayed', 'redirected', 'cancelled'],
  delayed: ['arrived', 'redirected', 'cancelled', 'expected'],
  arrived: ['received', 'refused', 'redirected'],
  redirected: ['arrived', 'cancelled'],
  received: [],
  refused: [],
  cancelled: [],
}

export interface OpsDelivery extends BaseDocument {
  docType: 'opsDelivery'
  carrier: string | null
  vendor: string | null
  description: string
  deliveryType: string
  schoolId: string | null
  destination: string
  expectedAt: string
  /** The person to tell on arrival. */
  contactStaffId: string | null
  contactName: string | null
  /** A purchase order this is part of. Linked for reference; Operations never marks it received. */
  purchaseOrderId?: string | null
  purchaseOrderNumber?: string | null
  specialHandling?: string | null
  status: DeliveryStatus
  arrivedAt?: string | null
  receivedByName?: string | null
  notes?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
  idempotencyKey?: string | null
}

/** Minutes past expected with no arrival; null when not late. */
export function deliveryLateBy(d: Pick<OpsDelivery, 'status' | 'expectedAt'>, nowIso: string): number | null {
  if (!['expected', 'delayed'].includes(d.status)) return null
  const late = Math.floor((Date.parse(nowIso) - Date.parse(d.expectedAt)) / 60_000)
  return late > 0 ? late : null
}

// ─────────────────────────────────────────────────────────────────────────────
// Alerts, log, handoff (stage 2)
// ─────────────────────────────────────────────────────────────────────────────

export const ALERT_KINDS = ['critical', 'urgent', 'overdue', 'information'] as const
export type AlertKind = (typeof ALERT_KINDS)[number]

/** Stable key for an alert so an acknowledgement sticks across refreshes. */
export const alertKey = (date: string, schoolId: string | null, component: string, entityId: string | null | undefined, text: string) => `${date}|${schoolId ?? 'district'}|${component}|${entityId ?? text.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60)}`

export function alertKindFor(severity: OpsSeverity, overdue = false): AlertKind {
  if (severity === 'critical') return 'critical'
  if (severity === 'urgent') return 'urgent'
  if (overdue) return 'overdue'
  return 'information'
}

export interface OpsAlertAck extends BaseDocument {
  docType: 'opsAlertAck'
  key: string
  date: string
  acknowledgedByName: string
  acknowledgedByUserId: string
  at: string
  note?: string | null
  issueId?: string | null
}

export interface OpsLogEntry extends BaseDocument {
  docType: 'opsLogEntry'
  date: string
  schoolId: string | null
  at: string
  byName: string
  text: string
  category: string
  /** Manual notes only; source events are read from their modules, never copied here. */
  kind: 'note'
}

export interface OpsHandoff extends BaseDocument {
  docType: 'opsHandoff'
  date: string
  schoolId: string | null
  shift: 'morning' | 'day' | 'evening' | 'overnight' | 'end_of_day'
  fromName: string
  fromUserId: string
  summary: string
  /** Open issue and task ids at the time of handoff, so the next shift sees what carried over. */
  openIssueIds: string[]
  openTaskIds: string[]
  toRole?: string | null
  acknowledgements: { byName: string; byUserId: string; at: string; note?: string | null }[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Daily readiness snapshot (stage 3) — written once per point in the day, never rewritten
// ─────────────────────────────────────────────────────────────────────────────

export interface DailyReadiness extends BaseDocument {
  docType: 'opsReadiness'
  date: string
  schoolId: string
  point: 'opening' | 'end_of_day'
  state: ReadinessState
  reasons: ReadinessReason[]
  unmet: string[]
  counts: Record<string, number>
  capturedAt: string
  capturedByName: string
}

/** Shipped inspection templates: generated by frequency at their location; each school may add its own. */
export const DEFAULT_INSPECTIONS: Omit<ChecklistTemplate, keyof BaseDocument | 'docType' | 'history'>[] = [
  {
    key: 'freezer-temp',
    name: 'Walk-in freezer temperature',
    kind: 'inspection',
    locationKind: 'kitchen',
    scope: 'district',
    schoolId: null,
    dayTypes: ['school_day', 'delayed_start', 'early_release', 'event_day', 'closed'],
    frequency: { freq: 'daily', time: '06:30' },
    responsibleRole: 'facilities_staff',
    status: 'active',
    version: 1,
    items: [
      item('freezer-reading', 'Walk-in freezer reading', 1, { category: 'food_service', dueTime: '06:30', verification: { type: 'numeric', min: -10, max: 0, unit: '°F' }, failureAction: 'issue_task', failureSeverity: 'urgent', criticalWhenClosed: true, locked: true }),
      item('cooler-reading', 'Walk-in cooler reading', 2, { category: 'food_service', dueTime: '06:30', verification: { type: 'numeric', min: 33, max: 41, unit: '°F' }, failureAction: 'issue_task', failureSeverity: 'urgent', criticalWhenClosed: true }),
    ],
  },
  {
    key: 'aed-monthly',
    name: 'AED and fire extinguisher check',
    kind: 'inspection',
    locationKind: 'athletic',
    scope: 'district',
    schoolId: null,
    dayTypes: ['school_day', 'delayed_start', 'early_release', 'event_day'],
    frequency: { freq: 'monthly', dayOfMonth: 1, time: '09:00' },
    responsibleRole: 'facilities_staff',
    status: 'active',
    version: 1,
    items: [
      item('aed-present', 'AED present, pads in date, status light green', 1, { category: 'safety', dueTime: '12:00', verification: { type: 'scan' }, failureAction: 'issue', failureSeverity: 'urgent', locked: true }),
      item('extinguishers', 'Extinguishers charged and tagged', 2, { category: 'safety', dueTime: '12:00', failureAction: 'work_order', failureSeverity: 'elevated' }),
    ],
  },
  {
    key: 'playground-weekly',
    name: 'Playground safety inspection',
    kind: 'inspection',
    locationKind: 'area',
    scope: 'district',
    schoolId: null,
    dayTypes: ['school_day'],
    frequency: { freq: 'weekly', days: [1], time: '07:30' },
    responsibleRole: 'facilities_staff',
    status: 'active',
    version: 1,
    items: [
      item('surfacing', 'Surfacing even; no hazards or debris', 1, { category: 'safety', dueTime: '08:00', verification: { type: 'photo' }, failureAction: 'work_order', failureSeverity: 'elevated' }),
      item('equipment', 'Equipment secure; no sharp edges or loose hardware', 2, { category: 'safety', dueTime: '08:00', failureAction: 'work_order', failureSeverity: 'urgent' }),
    ],
  },
]
